// A "Ducks"-style collection
// https://github.com/erikras/ducks-modular-redux
import { push, replace } from 'react-router-redux';

import { loadFieldDataFullPayload } from 'init/loadFieldDataFullPayload';
import { requestFieldData } from 'init/requestFieldData';
import { getManageRoute } from 'util/routes';
import { setActiveFieldUpdating } from 'api/loading';
import { omit } from 'lodash';
import { handleErrorsInQueue, registerError, treatErrorAs } from 'api/errors';
import { errorDisplayTypes, errorLevel } from 'constants/errors';

// constants
import { scriptMethods } from 'constants/scripts';
import { allOperationsSelected, farmerNavigationDefaultOperation } from 'constants/operations';
import { FIELD_VIEW_URL } from 'constants/urls';

// actions
import { setActiveProductRateLegend } from 'current/uiwidgets';
import { clearSelectedZones, addSelectedZone } from 'current/uiwidgets/selected-zones';
import { fetchCentroidForFieldId } from 'fields';
import { setSelectedJohnDeereField } from 'affiliates';

// selectors
import { getOverlayDateOf, getImagesOfLayerForField } from 'imagery/selectors';
import {
  getActiveFieldId,
  getActiveFieldUuid,
  getActiveOperationUuid,
  getActiveOverlayLayer,
  getActiveOverlayUuid,
  getActiveScriptId,
  getActiveUserId,
  getOverlayUuidGivenOffsetFromActive
} from 'current/selectors';
import { getDefaultCropId } from 'crops/selectors';
import {
  getFieldUuidById,
  getFieldIdByUuid,
  getFieldsForOperationSortedByName
} from 'fields/selectors';
import { userCanAccessOperation } from 'operations/selectors';


import { findFieldViewLegendForImage } from 'imagery/finders'; // TODO: Risk of circular deps

// Action Types
export const typePrefix = 'vra/current/';
export const actions = [
  'CLEAR_ACTIVE_SCRIPT_TYPE',
  'SET_ACTIVE_AFFILIATE_ORGANIZATION_ID',
  'SET_ACTIVE_CROP_ID',
  'SET_ACTIVE_CROP_PLAN',
  'SET_ACTIVE_FIELD_CENTROID',
  'SET_ACTIVE_FIELD_ID',
  'SET_ACTIVE_FIELD_UUID',
  'SET_ACTIVE_OPERATION_UUID',
  'SET_ACTIVE_OVERLAY_DATE',
  'SET_ACTIVE_OVERLAY_UUID',
  'SET_ACTIVE_OVERLAY_LAYER',
  'SET_ACTIVE_PRODUCT_YEAR',
  'SET_ACTIVE_SCRIPT_ID',
  'SET_ACTIVE_SCRIPT_METHOD',
  'SET_ACTIVE_SCRIPT_TYPE',
  'SET_ACTIVE_USER_ID',
  'SET_ACTIVE_ZONE_ID',
  'SET_CURRENT_MAP_ZOOM',
  'SET_FATAL_ERROR',
  'SET_SCRIPT_IS_FETCHING',
  'SET_TRIAL_PLACEMENT_OPTION',
  'SET_ACTIVE_CROP_FOR_CURRENT_MAP_LAYER'
].reduce((result, key) => { result[key] = `${typePrefix}${key}`; return result; }, {});

// Reducer

export default function reducer( // NOSONAR False-Positive, Reducer Exception
  state = {
    cropId: getDefaultCropId(),
    cropCurrentMapLayer: undefined,
    managementZones: [],
    products: {},
    scriptIsFetching: false,
    scriptMethod: scriptMethods.MANUAL
  },
  action = {}
) {
  switch (action.type) {
    case actions.SET_ACTIVE_CROP_ID:
      return { ...state, cropId: action.payload.cropId };
    case actions.SET_ACTIVE_CROP_FOR_CURRENT_MAP_LAYER:
      return { ...state, cropCurrentMapLayer: action.payload.cropCurrentMapLayer };
    case actions.SET_ACTIVE_CROP_PLAN:
      return { ...state, cropPlan: action.payload.cropPlan };
    case actions.SET_ACTIVE_FIELD_ID:
      return { ...state, fieldId: action.payload.fieldId };
    case actions.SET_ACTIVE_FIELD_UUID:
      return { ...state, fieldUuid: action.payload.fieldUuid };
    case actions.SET_ACTIVE_USER_ID:
      return { ...state, userId: action.payload.userId };
    case actions.SET_ACTIVE_SCRIPT_ID:
      return { ...state, scriptId: action.payload.scriptId };
    case actions.SET_ACTIVE_SCRIPT_METHOD:
      return { ...state, scriptMethod: action.payload.scriptMethod };
    case actions.SET_ACTIVE_OPERATION_UUID:
      return { ...state, operationUuid: action.payload.operationUuid };
    case actions.SET_ACTIVE_PRODUCT_YEAR:
      return { ...state, productYear: action.payload.productYear };
    case actions.SET_ACTIVE_AFFILIATE_ORGANIZATION_ID:
      return { ...state, affiliateOrganizationId: action.payload };
    case actions.SET_ACTIVE_ZONE_ID:
      return { ...state, zoneId: action.payload.zoneId };
    case actions.SET_CURRENT_MAP_ZOOM:
      return { ...state, mapZoom: action.payload.mapZoom };
    case actions.SET_ACTIVE_OVERLAY_LAYER:
      return { ...state, overlayLayer: action.payload.overlayLayer };
    case actions.SET_ACTIVE_OVERLAY_UUID:
      return { ...state, overlayUuid: action.payload.overlayUuid };
    case actions.SET_ACTIVE_OVERLAY_DATE:
      return { ...state, overlayDate: action.payload.overlayDate };
    case actions.SET_ACTIVE_FIELD_CENTROID:
      return { ...state, fieldCentroid: action.payload.fieldCentroid };
    case actions.SET_FATAL_ERROR:
      return { ...state, fatalError: action.payload.fatalError };
    case actions.SET_ACTIVE_SCRIPT_TYPE:
      return { ...state, scriptType: action.payload.scriptType };
    case actions.CLEAR_ACTIVE_SCRIPT_TYPE:
      return omit(state, ['scriptType']);
    case actions.SET_SCRIPT_IS_FETCHING:
      return { ...state, scriptIsFetching: action.payload.scriptIsFetching };
    case actions.SET_TRIAL_PLACEMENT_OPTION:
      return { ...state, selectedTrialPlacementId: action.payload.id };
    default:
      return state;
  }
}

// Action Creators

export const clearActiveAffiliateOrganizationId = () => {
  return {
    type: actions.SET_ACTIVE_AFFILIATE_ORGANIZATION_ID
  };
};

export const setActiveCropId = (cropId) => {
  return {
    type: actions.SET_ACTIVE_CROP_ID,
    payload: { cropId }
  };
};

export const setActiveCropForCurrentMapLayer = (cropCurrentMapLayer) => {
  return {
    type: actions.SET_ACTIVE_CROP_FOR_CURRENT_MAP_LAYER,
    payload: { cropCurrentMapLayer }
  };
};

const setActiveFieldId = (fieldId) => {
  // see setActiveFieldIdentifier for general use
  return {
    type: actions.SET_ACTIVE_FIELD_ID,
    payload: { fieldId }
  };
};

const setActiveFieldUuid = (fieldUuid) => {
  // see setActiveFieldIdentifier for general use
  return {
    type: actions.SET_ACTIVE_FIELD_UUID,
    payload: { fieldUuid }
  };
};

export const setActiveFieldIdentifier = ({ fieldId, fieldUuid }) => {
  return (dispatch, getState) => {
    if (!fieldId && !fieldUuid) {
      throw new Error('at least one field indentifier required');
    }

    const state = getState();
    fieldId = fieldId || getFieldIdByUuid(state, fieldUuid);
    fieldUuid = fieldUuid || getFieldUuidById(state, fieldId);
    dispatch(setActiveFieldId(fieldId));
    dispatch(setActiveFieldUuid(fieldUuid));
  };
};

/** Set the active field, and also fetch other related data including prescriptions, field features, boundaries,
    and planting events and update the store with it. */
export const setActiveFieldAndFetchRelated = (fieldUuid) => {
  return (dispatch, getState) => {
    const fieldId = getFieldIdByUuid(getState(), fieldUuid);
    dispatch(setActiveFieldUpdating());
    loadFieldDataFullPayload({ dispatch, getState, fieldId, fieldUuid, ignoreRoute: true })
    .then(() => {
      dispatch(setActiveFieldId(fieldId));
      dispatch(setActiveFieldUuid(fieldUuid));
    })
    .catch(treatErrorAs({ severity: errorLevel.WARN }));
  };
};

// If the selected operation is "all operations", then operationUuid should be ""
export const setActiveOperationUuid = (operationUuid) => {
  return (dispatch, getState) => {
    const state = getState();

    operationUuid = operationUuid !== farmerNavigationDefaultOperation ? operationUuid : allOperationsSelected;
    if (operationUuid && !userCanAccessOperation(state, operationUuid)) {
      operationUuid = allOperationsSelected; // stale operation indicator, force to all operations
    }

    const currentOperationUuid = getActiveOperationUuid(state);
    if (currentOperationUuid === operationUuid) {
      return;
    }

    const fieldUuid = getActiveFieldUuid(state);
    const fields = getFieldsForOperationSortedByName(state, operationUuid);
    const activeFieldInOperation = fields.find(field => field.uuid === fieldUuid);
    
    if (fields.length && !activeFieldInOperation) {
      dispatch(setActiveFieldAndFetchRelated(fields[0].uuid));
    }
    
    dispatch({
      type: actions.SET_ACTIVE_OPERATION_UUID,
      payload: { operationUuid }
    });
    /* 
    * If the selected operation has no fields, we show to the user
    * the same message we show when Prescriptions is clicked on 
    * the nav bar for an operation without fields.
    */
    if (!fields.length) {
      registerError({
        actions: [
          {
            textKey: 'button-text__create-fields',
            action: () => { window.location = FIELD_VIEW_URL.FIELD_SELECTION; }
          },
          {
            textKey: 'button-text__upload-fields',
            action: () => { window.location = FIELD_VIEW_URL.DATA_INBOX; }
          }
        ],
        message: 'No fields in selected operation',
        messageKey: 'help-text__no-fields',
        severity: errorLevel.BLOCK,
        displayErrorAs: errorDisplayTypes.MODAL
      });
      return handleErrorsInQueue();
    }
  };
};

export const setActiveUserId = (userId) => {
  return {
    type: actions.SET_ACTIVE_USER_ID,
    payload: { userId }
  };
};

export const setActiveScriptId = (scriptId) => {
  return (dispatch) => {
    dispatch({
      type: actions.SET_ACTIVE_SCRIPT_ID,
      payload: { scriptId }
    });
    dispatch(setActiveProductRateLegend());
  };
};

export const setActiveProductYear = (productYear) => {
  return {
    type: actions.SET_ACTIVE_PRODUCT_YEAR,
    payload: { productYear }
  };
};

export const setActiveAffiliateOrganizationId = (affiliateOrganizationId) => {
  return {
    type: actions.SET_ACTIVE_AFFILIATE_ORGANIZATION_ID,
    payload: affiliateOrganizationId
  };
};

export const setCurrentMapZoom = (mapZoom) => {
  return {
    type: actions.SET_CURRENT_MAP_ZOOM,
    payload: { mapZoom }
  };
};

export const setActiveZoneId = ({ zoneId }) => {
  return (dispatch) => {
    dispatch(clearSelectedZones());
    dispatch(addSelectedZone(zoneId));
    dispatch({
      type: actions.SET_ACTIVE_ZONE_ID,
      payload: { zoneId }
    });
  };
};

export const setActiveOverlayUuid = (overlayUuid) => {
  return {
    type: actions.SET_ACTIVE_OVERLAY_UUID,
    payload: {
      overlayUuid
    }
  };
};

export const setActiveOverlayDate = (overlayDate) => {
  return {
    type: actions.SET_ACTIVE_OVERLAY_DATE,
    payload: {
      overlayDate
    }
  };
};

export const setActiveOverlayLayer = (overlayLayer) => {
  return {
    type: actions.SET_ACTIVE_OVERLAY_LAYER,
    payload: {
      overlayLayer
    }
  };
};

export const setActiveOverlayInfo = ({ uuid, layer }) => {
  return (dispatch, getState) => {
    dispatch(setActiveOverlayLayer(layer));
    dispatch(setActiveOverlayUuid(uuid));
    const state = getState();
    const fieldId = getActiveFieldId(state);
    const date = getOverlayDateOf({ state, uuid, layer, fieldId });
    dispatch(setActiveOverlayDate(date));
  };
};

export const setActiveOverlayGivenOffsetFromActive = (offset) => {
  return (dispatch, getState) => {
    const state = getState();
    const layer = getActiveOverlayLayer(state);
    const uuid = getOverlayUuidGivenOffsetFromActive(state, offset);
    dispatch(setActiveOverlayInfo({ uuid, layer }));
  };
};

export const loadActiveFieldViewImageLegend = () => {
  return (dispatch, getState) => {
    const state = getState();
    const layer = getActiveOverlayLayer(state);
    const uuid = getActiveOverlayUuid(state);
    const fieldId = getActiveFieldId(state);
    const image = getImagesOfLayerForField({ state, fieldId, layer, uuid });
    if (image.fieldViewLayer) {
      findFieldViewLegendForImage({ dispatch, getState, image });
    }
  };
};

export const setActiveFieldCentroid = (fieldCentroid) => {
  return {
    type: actions.SET_ACTIVE_FIELD_CENTROID,
    payload: { fieldCentroid }
  };
};

// TODO: convert to use the pendingResource check
export const setScriptIsFetching = (scriptIsFetching) => {
  return {
    type: actions.SET_SCRIPT_IS_FETCHING,
    payload: { scriptIsFetching }
  };
};

export const fetchCentroidForActiveFieldId = () => {
  return (dispatch, getState) => {
    const state = getState();
    const userId = getActiveUserId(state);
    const fieldId = getActiveFieldId(state);

    fetchCentroidForFieldId({ fieldId, userId })
    .then((fieldCentroid) => {
      dispatch(setActiveFieldCentroid(fieldCentroid));
    });
  };
};

export const changeActiveScriptInManage = (scriptId) => {
  return (dispatch, getState) => {
    const currentScriptId = getActiveScriptId(getState());
    if (scriptId !== currentScriptId) {
      dispatch(setActiveScriptId(scriptId));
      dispatch(replace(getManageRoute(getState())));
    }
  };
};

export const setFatalError = (fatalError) => {
  return {
    type: actions.SET_FATAL_ERROR,
    payload: { fatalError }
  };
};

export const setActiveScriptType = (scriptType) => {
  return {
    type: actions.SET_ACTIVE_SCRIPT_TYPE,
    payload: { scriptType }
  };
};

export const clearActiveScriptType = () => {
  return {
    type: actions.CLEAR_ACTIVE_SCRIPT_TYPE
  };
};

export const setActiveScriptMethod = (scriptMethod) => {
  return {
    type: actions.SET_ACTIVE_SCRIPT_METHOD,
    payload: { scriptMethod }
  };
};

/** Set the active field, and also fetch other related data including prescriptions, field features, boundaries,
    and planting events and update the store with it. */
export const updateActiveField = (fieldUuid) => {
  return (dispatch, getState) => {
    const fieldId = getFieldIdByUuid(getState(), fieldUuid);
    dispatch(setActiveFieldUpdating());
    return requestFieldData({ dispatch, getState, fieldId, fieldUuid })
    .then(() => {
      // set to null in case we just switched from another field --
      // we don't want an active script hanging around from a different field
      dispatch(setActiveScriptId(null));
      dispatch(setActiveFieldId(fieldId));
      dispatch(setActiveFieldUuid(fieldUuid));
      dispatch(setSelectedJohnDeereField());

      dispatch(push(getManageRoute(getState())));
    })
    .catch(treatErrorAs({ severity: errorLevel.WARN }));
  };
};

export const setTrialPlacementOption = (id) => {
  return {
    type: actions.SET_TRIAL_PLACEMENT_OPTION,
    payload: {
      id
    }
  };
};

export const setActiveCropPlan = (cropPlan) => {
  return {
    type: actions.SET_ACTIVE_CROP_PLAN,
    payload: {
      cropPlan
    }
  };
};
